import { Component, type ErrorInfo, type ReactNode } from "react"

/**
 * Top-Level React-Error-Boundary (Phase 7a, 2026-05-08).
 *
 * Faengt uncaught Errors in der Component-Tree, schickt sie zu Sentry (wenn
 * konfiguriert) und zeigt einen freundlichen Fallback statt weisser Seite.
 *
 * Vor Phase 7a: jeder Render-Crash machte die App komplett unsichtbar — User
 * sah weisse Seite, du sahst nichts. Jetzt: Fallback-UI mit Reload-Button und
 * Sentry-Eintrag mit Stacktrace + Komponenten-Tree.
 */

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Ins eigene Activity-Log — der Weg, der IMMER funktioniert.
    // Sentry ist nur aktiv, wenn beim Build eine DSN gesetzt war; im
    // CI-Build ist keine gesetzt. Bis 10.08.2026 hiess das: der Dialog
    // versprach „wurde automatisch gemeldet", und niemand bekam etwas.
    // Der React-#185-Crash im Mail-Reiter war deshalb nur ueber einen
    // Screenshot auffindbar.
    try {
      fetch("/api/v1/client-errors", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: String(error?.message || error).slice(0, 4000),
          url: window.location.href,
          component_stack: (info.componentStack || "").slice(0, 20000),
          user_agent: navigator.userAgent,
        }),
        keepalive: true,
      }).catch(() => { /* Melden darf nie die Fehleranzeige kaputtmachen */ })
    } catch { /* no-op */ }
    // Sentry capture wenn @sentry/react verfuegbar ist (lazy import damit
    // ErrorBoundary auch ohne Sentry funktioniert)
    import("@sentry/react").then((Sentry) => {
      try {
        Sentry.captureException(error, {
          contexts: {
            react: { componentStack: info.componentStack },
          },
        })
      } catch { /* no-op */ }
    }).catch(() => { /* no Sentry, no problem */ })
    // Im Console damit's auch lokal sichtbar bleibt
    console.error("[ErrorBoundary]", error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh", display: "flex", alignItems: "center",
            justifyContent: "center", background: "#f8fafc", padding: 24,
          }}
        >
          <div
            style={{
              maxWidth: 480, width: "100%", padding: 32,
              background: "#fff", borderRadius: 12,
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
              border: "1px solid #e5e7eb",
            }}
          >
            <h1 style={{ fontSize: 20, fontWeight: 600, color: "#dc2626", marginBottom: 12 }}>
              Etwas ist schiefgelaufen
            </h1>
            <p style={{ fontSize: 14, color: "#475569", lineHeight: 1.5, marginBottom: 16 }}>
              Die App ist auf einen unerwarteten Fehler gestossen. Der Fehler wurde
              automatisch gemeldet. Bitte lade die Seite neu — falls das Problem
              bleibt, sag bescheid.
            </p>
            {this.state.error?.message && (
              <pre
                style={{
                  fontSize: 12, background: "#f1f5f9", color: "#334155",
                  padding: 12, borderRadius: 6, marginBottom: 16,
                  whiteSpace: "pre-wrap", wordBreak: "break-word",
                  maxHeight: 200, overflow: "auto",
                }}
              >
                {this.state.error.message}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                width: "100%", padding: "10px 16px",
                background: "#1d4ed8", color: "#fff", border: "none",
                borderRadius: 6, cursor: "pointer",
                fontSize: 14, fontWeight: 600,
              }}
            >
              Seite neu laden
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}
